// eslint-disable-next-line no-redeclare
/* global google */
import isFinite from 'lodash/isFinite';
import noop from 'lodash/noop';

import {
	CIRCLE_OPTIONS,
	DISABLED_INTERACTIONS_CONFIG,
	ENABLED_INTERACTIONS_CONFIG,
	MAX_LAT,
	MAX_LNG,
	MIN_RADIUS_ZOOM_LEVEL,
} from 'Components/shared/GoogleMap/GoogleMap.consts';
import type { Location, LocationCoords } from 'Components/shared/GoogleMap/GoogleMap.types';
import {
	triggerUserZoomInVehicleLocationEvent,
	triggerUserZoomOutVehicleLocationEvent,
} from 'Services/analytics/events';

import type {
	GetBoundsProps,
	GetInteractionConfigProps,
	GetMapProps,
	GetMarkerProps,
	InteractionsConfig,
	OnZoomChangedProps,
	ReportZoomEventProps,
} from './useGoogleMap.types';

let areaCircle = {
	setMap: noop,
};

export const reportZoomEvent = ({ eventId, lastZoomLevel, zoomLevel }: ReportZoomEventProps): void => {
	const isZoomIn = lastZoomLevel < zoomLevel;

	if (isZoomIn) {
		triggerUserZoomInVehicleLocationEvent(eventId);
	} else {
		triggerUserZoomOutVehicleLocationEvent(eventId);
	}
};

export const onZoomChanged = ({ callback, center, Circle, map }: OnZoomChangedProps): void => {
	const zoomLevel = map.getZoom();
	if (!zoomLevel) {
		areaCircle.setMap(null);
		return;
	}

	callback(zoomLevel);
	if (zoomLevel >= MIN_RADIUS_ZOOM_LEVEL) {
		areaCircle.setMap(null);
		areaCircle = new Circle({ center, map, ...CIRCLE_OPTIONS });
	}
};

export const isValidLatitude = (lat: number): boolean => isFinite(lat) && Math.abs(lat) <= MAX_LAT;

export const isValidLongitude = (lng: number): boolean => isFinite(lng) && Math.abs(lng) <= MAX_LNG;

export const isValidLocation = ({ lat, lng }: LocationCoords): boolean => isValidLatitude(lat) && isValidLongitude(lng);

export const generateMap = ({
	interactionsConfig,
	isCoarsePointer,
	Map: GoogleMap,
	mapElement,
	mapOptions,
}: GetMapProps): google.maps.Map =>
	new GoogleMap(mapElement, {
		...mapOptions,
		...interactionsConfig,
		disableDefaultUI: isCoarsePointer,
		zoomControl: !isCoarsePointer,
	});

export const generateMarkers = ({ LatLng, locations, map, Marker, Point }: GetMarkerProps): google.maps.Marker[] =>
	locations.map(
		({ icon, isPrimary, lat, lng }) =>
			new Marker({
				clickable: false,
				icon: {
					anchor: new Point(20, 20),
					url: icon,
				},
				map,
				position: new LatLng({ lat, lng }),
				zIndex: isPrimary ? 1 : 0,
			}),
	);

export const getBoundsFromMarkers = ({ bounds, markers }: GetBoundsProps): google.maps.LatLngBounds =>
	markers.reduce((acc, marker) => {
		const markerPosition = marker.getPosition();
		if (markerPosition) {
			acc.extend(markerPosition);
		}
		return acc;
	}, bounds);

export const getInteractionConfig = ({ isCoarsePointer, isExpanded }: GetInteractionConfigProps): InteractionsConfig =>
	isCoarsePointer && !isExpanded ? DISABLED_INTERACTIONS_CONFIG : ENABLED_INTERACTIONS_CONFIG;

export const getPrimaryLocation = (locations: Location[]): Location | undefined =>
	locations.find(({ isPrimary }) => isPrimary === true);
